import { addDays, addSeconds, compareAsc, format } from 'date-fns';
import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loading } from '../../../components/shared/Loading';
import { dateToApiFormat, parseDateOnly } from '../../../helpers/dateHelper';
import { useGetMyPublishedShiftsForDateRangeQuery } from '../../../services/gql/graphql.generated';
import { ShiftData } from './nextShift.types';
import { NextShiftCard } from './NextShiftCard';

export const NextShiftSection: FC = () => {
  const [{ data, fetching }] = useGetMyPublishedShiftsForDateRangeQuery({
    variables: {
      range: {
        start: dateToApiFormat(new Date()),
        // We want to include the end date, so query 31 days ahead of today.
        end: dateToApiFormat(addDays(new Date(), 31))
      }
    },
    context: useMemo(() => ({ suspense: false }), []),
    requestPolicy: 'network-only'
  });

  const navigate = useNavigate();

  const nextShift = useMemo<ShiftData | undefined>(() => {
    const shifts = data?.myPublishedShiftsInDateRange?.shifts ?? [];

    return [...shifts]
      .map((shift): ShiftData => {
        const date = parseDateOnly(shift.date);
        const startTime = addSeconds(date, shift.start);
        const endTime = addSeconds(date, shift.end);
        return {
          date: format(date, 'EEEE do LLLL'),
          startTime: format(startTime, 'HH:mm'),
          endTime: format(endTime, 'HH:mm'),
          startTimeRaw: startTime,
          endTimeRaw: endTime
        };
      })
      .sort((a, b) => compareAsc(a.startTimeRaw, b.startTimeRaw))[0];
  }, [data]);

  if (fetching) return <Loading />;
  if (!nextShift) return null;

  return (
    <NextShiftCard
      shift={nextShift}
      onViewDetails={() => navigate(`/ess/my-calendar/${dateToApiFormat(nextShift.startTimeRaw)}/shift-details`)}
    />
  );
};
